import { useState } from 'react';
import { removeUserFromWorkspace } from '../../../apiServices/userApi';
import { Button } from '../../ui/Button';
import { Spinner } from '../../ui/Spinner';
import { useNotification } from '@/hooks/useNotification';

interface ExitWorkspaceModalProps {
  workspace: { id: { S: string }; title: { S: string } };
  closeModal: () => void;
  currentUserInfo?: { user: { S: string } };
  updateIsQuerying: (q: boolean) => void;
  refreshList: () => void;
}

export default function ExitWorkspaceModal({ workspace, closeModal, currentUserInfo, updateIsQuerying, refreshList }: ExitWorkspaceModalProps) {
  const [loading, setLoading] = useState(false);
  const { notify } = useNotification();

  const exitFromGroup = async () => {
    const uid = currentUserInfo?.user?.S;
    if (!uid) {
      // Guard instead of throwing on currentUserInfo!.user.S — a missing prop
      // made exiting fail before the request was even sent
      notify({ type: 'error', message: `Could not exit from the group '${workspace.title.S}'` });
      closeModal();
      return;
    }
    setLoading(true);
    try {
      updateIsQuerying(true);
      const res = await removeUserFromWorkspace(uid, workspace.id.S);
      updateIsQuerying(false);
      // Backend signals failure with HTTP 200 + "ERROR: …" body (e.g. the
      // creator/last member is not allowed to leave) — don't toast success
      if (typeof res?.response === 'string' && res.response.includes('ERROR')) {
        const message = res.response.includes('not allowed')
          ? `The workspace creator can't leave "${workspace.title.S}"`
          : `Could not exit from the group '${workspace.title.S}'`;
        notify({ type: 'error', message });
        setLoading(false);
        closeModal();
        return;
      }
      notify({ type: 'success', message: `Exited from group ${workspace.title.S}` });
      refreshList();
    } catch {
      updateIsQuerying(false);
      notify({ type: 'error', message: `Could not exit from the group '${workspace.title.S}'` });
    }
    setLoading(false);
    closeModal();
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">{`Leave workspace "${workspace.title.S}"?`}</p>
      <Button variant="destructive" onClick={exitFromGroup} disabled={loading}>
        {loading && <Spinner size="sm" />}
        Confirm
      </Button>
    </div>
  );
}
